import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  arrayUnion,
  arrayRemove,
  Timestamp,
  serverTimestamp,
  QueryDocumentSnapshot,
  writeBatch
} from '@firebase/firestore';
import { db } from '../config/firebase';
import { FriendRequest, UserProfile } from '../types/firebase';
import { createDefaultUserProfile } from './userProfileService';

const FRIEND_REQUESTS_COLLECTION = 'friendRequests';
const USERS_COLLECTION = 'users';

export const sendFriendRequest = async (senderId: string, receiverId: string): Promise<void> => {
  try {
    // Check if request already exists
    const existingRequest = await checkExistingRequest(senderId, receiverId);
    if (existingRequest) {
      throw new Error('Friend request already exists');
    }

    const senderRef = doc(db, USERS_COLLECTION, senderId);
    const receiverRef = doc(db, USERS_COLLECTION, receiverId);
    
    // Get both user documents
    const [senderDoc, receiverDoc] = await Promise.all([
      getDoc(senderRef),
      getDoc(receiverRef)
    ]);

    if (!senderDoc.exists()) {
      throw new Error('Sender profile not found');
    }
    if (!receiverDoc.exists()) {
      throw new Error('Receiver profile not found');
    }

    const senderData = senderDoc.data();
    const receiverData = receiverDoc.data();

    // Initialize friend request arrays if they don't exist
    const senderUpdate: any = {
      updatedAt: serverTimestamp()
    };
    
    if (!senderData.friendRequests) {
      senderUpdate.friendRequests = {
        sent: [receiverId],
        received: []
      };
    } else {
      if (!senderData.friendRequests.sent) {
        senderUpdate['friendRequests.sent'] = [receiverId];
      } else {
        senderUpdate['friendRequests.sent'] = arrayUnion(receiverId);
      }
    }

    const receiverUpdate: any = {
      updatedAt: serverTimestamp()
    };
    
    if (!receiverData.friendRequests) {
      receiverUpdate.friendRequests = {
        sent: [],
        received: [senderId]
      };
    } else {
      if (!receiverData.friendRequests.received) {
        receiverUpdate['friendRequests.received'] = [senderId];
      } else {
        receiverUpdate['friendRequests.received'] = arrayUnion(senderId);
      }
    }

    // Create the friend request document
    const friendRequestRef = doc(collection(db, FRIEND_REQUESTS_COLLECTION));
    const friendRequestData = {
      senderId,
      receiverId,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Perform all updates in a batch
    const batch = writeBatch(db);
    batch.update(senderRef, senderUpdate);
    batch.update(receiverRef, receiverUpdate);
    batch.set(friendRequestRef, friendRequestData);
    
    await batch.commit();
  } catch (error) {
    console.error('Error sending friend request:', error);
    throw error;
  }
};

export const respondToFriendRequest = async (
  requestId: string,
  response: 'accepted' | 'rejected',
  currentUserId: string
): Promise<void> => {
  try {
    const requestRef = doc(db, FRIEND_REQUESTS_COLLECTION, requestId);
    const requestDoc = await getDoc(requestRef);
    
    if (!requestDoc.exists()) {
      throw new Error('Friend request not found');
    }
    
    const request = requestDoc.data() as FriendRequest;

    if (request.receiverId !== currentUserId) {
      throw new Error('Unauthorized to respond to this request');
    }

    const batch = writeBatch(db);

    // Update the friend request status
    batch.update(requestRef, {
      status: response,
      updatedAt: serverTimestamp()
    });

    if (response === 'accepted') {
      // Add each user to the other's friends list
      batch.update(doc(db, USERS_COLLECTION, request.senderId), {
        friends: arrayUnion(request.receiverId),
        updatedAt: serverTimestamp()
      });

      batch.update(doc(db, USERS_COLLECTION, request.receiverId), {
        friends: arrayUnion(request.senderId),
        updatedAt: serverTimestamp()
      });
    }

    // Remove request from both users' friendRequests arrays (using userId, not requestId)
    batch.update(doc(db, USERS_COLLECTION, request.senderId), {
      'friendRequests.sent': arrayRemove(request.receiverId),
      updatedAt: serverTimestamp()
    });

    batch.update(doc(db, USERS_COLLECTION, request.receiverId), {
      'friendRequests.received': arrayRemove(request.senderId),
      updatedAt: serverTimestamp()
    });

    // Delete the friend request document from the collection
    batch.delete(requestRef);

    await batch.commit();
  } catch (error) {
    console.error('Error responding to friend request:', error);
    throw error;
  }
};

export const removeFriend = async (userId: string, friendId: string): Promise<void> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const friendRef = doc(db, USERS_COLLECTION, friendId);

    // Get both user documents to verify they exist and are friends
    const [userDoc, friendDoc] = await Promise.all([
      getDoc(userRef),
      getDoc(friendRef)
    ]);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    if (!friendDoc.exists()) {
      throw new Error('Friend not found');
    }

    const userData = userDoc.data();
    const friendData = friendDoc.data();

    // Verify they are actually friends
    if (!userData.friends?.includes(friendId) || !friendData.friends?.includes(userId)) {
      throw new Error('Users are not friends');
    }

    // Use a batch to ensure both updates happen or neither does
    const batch = writeBatch(db);

    // Remove from both users' friends arrays and update timestamps
    batch.update(userRef, {
      friends: arrayRemove(friendId),
      updatedAt: serverTimestamp()
    });

    batch.update(friendRef, {
      friends: arrayRemove(userId),
      updatedAt: serverTimestamp()
    });

    await batch.commit();
  } catch (error) {
    console.error('Error removing friend:', error);
    throw error;
  }
};

export const getFriendRequests = async (userId: string): Promise<FriendRequest[]> => {
  try {
    const q = query(
      collection(db, FRIEND_REQUESTS_COLLECTION),
      where('receiverId', '==', userId),
      where('status', '==', 'pending')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc: QueryDocumentSnapshot) => ({
      id: doc.id,
      ...doc.data(),
      createdAt: (doc.data().createdAt as Timestamp).toDate(),
      updatedAt: (doc.data().updatedAt as Timestamp).toDate(),
    })) as FriendRequest[];
  } catch (error) {
    console.error('Error getting friend requests:', error);
    throw error;
  }
};

export const getFriendsList = async (userId: string): Promise<UserProfile[]> => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data() as UserProfile;
    
    if (!userData.friends || userData.friends.length === 0) {
      return [];
    }

    const friendProfiles = await Promise.all(
      userData.friends.map(async (friendId) => {
        try {
          const friendDoc = await getDoc(doc(db, USERS_COLLECTION, friendId));
          if (!friendDoc.exists()) {
            console.warn(`Friend document ${friendId} does not exist`);
            return createDefaultUserProfile(
              friendId,
              'Unknown User',
              '',
              ''
            );
          }
          
          const friendData = friendDoc.data();
          return createDefaultUserProfile(
            friendDoc.id,
            friendData?.displayName || friendData?.name || 'Unknown User',
            friendData?.email || '',
            friendData?.photoURL || friendData?.avatar || ''
          );
        } catch (error) {
          console.error(`Error loading friend ${friendId}:`, error);
          return null;
        }
      })
    );

    // Filter out any null values from failed friend loads
    return friendProfiles.filter((profile): profile is UserProfile => profile !== null);
  } catch (error) {
    console.error('Error getting friends list:', error);
    throw error;
  }
};

const checkExistingRequest = async (senderId: string, receiverId: string): Promise<boolean> => {
  try {
    // Get both users' documents
    const senderDoc = await getDoc(doc(db, USERS_COLLECTION, senderId));
    const receiverDoc = await getDoc(doc(db, USERS_COLLECTION, receiverId));

    if (!senderDoc.exists() || !receiverDoc.exists()) {
      throw new Error('One or both users not found');
    }

    const senderData = senderDoc.data();
    const receiverData = receiverDoc.data();

    // Check if they are already friends
    if (senderData.friends?.includes(receiverId) || receiverData.friends?.includes(senderId)) {
      return true;
    }

    // Check if a request already exists in either direction
    const senderHasSentRequest = senderData.friendRequests?.sent?.includes(receiverId);
    const receiverHasSentRequest = receiverData.friendRequests?.sent?.includes(senderId);
    const senderHasReceivedRequest = senderData.friendRequests?.received?.includes(receiverId);
    const receiverHasReceivedRequest = receiverData.friendRequests?.received?.includes(senderId);

    // A request exists if:
    // 1. Sender has sent AND receiver has received, OR
    // 2. Receiver has sent AND sender has received
    return (senderHasSentRequest && receiverHasReceivedRequest) || 
           (receiverHasSentRequest && senderHasReceivedRequest);
  } catch (error) {
    console.error('Error checking existing request:', error);
    throw error;
  }
};

export const cancelFriendRequest = async (senderId: string, receiverId: string): Promise<void> => {
  try {
    // Find the friend request document
    const q = query(
      collection(db, FRIEND_REQUESTS_COLLECTION),
      where('senderId', '==', senderId),
      where('receiverId', '==', receiverId),
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    
    const batch = writeBatch(db);
    
    // Remove from sender's sent requests
    batch.update(doc(db, USERS_COLLECTION, senderId), {
      'friendRequests.sent': arrayRemove(receiverId),
      updatedAt: serverTimestamp()
    });

    // Remove from receiver's received requests
    batch.update(doc(db, USERS_COLLECTION, receiverId), {
      'friendRequests.received': arrayRemove(senderId),
      updatedAt: serverTimestamp()
    });
    
    // Delete the friend request document(s) from the collection
    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    await batch.commit();
  } catch (error) {
    console.error('Error canceling friend request:', error);
    throw error;
  }
}; 

export const cleanupOldFriendRequests = async (): Promise<void> => {
  try {
    // Find all pending friend requests older than 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const q = query(
      collection(db, FRIEND_REQUESTS_COLLECTION),
      where('status', '==', 'pending'),
      where('createdAt', '<', Timestamp.fromDate(thirtyDaysAgo))
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log('No old friend requests to clean up');
      return;
    }
    
    const batch = writeBatch(db);
    
    for (const docSnapshot of querySnapshot.docs) {
      const request = docSnapshot.data() as FriendRequest;
      
      // Remove from both users' friendRequests arrays
      batch.update(doc(db, USERS_COLLECTION, request.senderId), {
        'friendRequests.sent': arrayRemove(request.receiverId),
        updatedAt: serverTimestamp()
      });
      
      batch.update(doc(db, USERS_COLLECTION, request.receiverId), {
        'friendRequests.received': arrayRemove(request.senderId),
        updatedAt: serverTimestamp()
      });
      
      // Delete the friend request document
      batch.delete(docSnapshot.ref);
    }
    
    await batch.commit();
    console.log(`Cleaned up ${querySnapshot.size} old friend requests`);
  } catch (error) {
    console.error('Error cleaning up old friend requests:', error);
    throw error;
  }
}; 